import { useFilterStore, useStateCounts } from "~/components/build/Show/lib/useFilterStore";
import { FilterByOption } from "~/components/build/Show/lib/filterConstants";
import { useBuildPreferencesStore } from "~/components/build/Show/lib/useBuildPreferencesStore";
import { useBuildPageStore } from "~/components/build/Show/lib/useBuildPageStore";

/**
 * Displays summaries of steps hidden by display options and/or active filters.
 * Centralises count calculations and renders both summaries inline with a
 * divider between them when both are visible.
 */
export function HiddenStepsSummary({ filters }: { filters: FilterByOption[] }) {
  const resetFilters = useFilterStore((state) => state.resetFilters);
  const showSkippedSteps = useBuildPreferencesStore((state) => state.showSkippedSteps);
  const setDisplayOptionsOpen = useBuildPageStore((state) => state.setDisplayOptionsOpen);
  const stateCounts = useStateCounts();

  const skippedCount = stateCounts[FilterByOption.Ignored];
  const stepsHiddenByDisplayOptions = !showSkippedSteps && skippedCount > 0 ? skippedCount : 0;

  // Exclude skipped steps from the filter total when they're already
  // accounted for by display options.
  const { [FilterByOption.Ignored]: _ignored, ...visibleStateCounts } = stateCounts;
  const countableStates = showSkippedSteps ? stateCounts : visibleStateCounts;
  const totalSteps = Object.values(countableStates).reduce((sum, count) => sum + count, 0);
  const visibleSteps = filters.reduce((sum, filter) => sum + (stateCounts[filter] || 0), 0);
  const stepsHiddenByFilters = filters.length > 0 ? Math.max(totalSteps - visibleSteps, 0) : 0;

  if (stepsHiddenByDisplayOptions === 0 && stepsHiddenByFilters === 0) {
    return null;
  }

  return (
    <div
      className="text-charcoal-300 mt-4 flex flex-wrap items-center justify-center gap-y-1 py-2 text-xs"
      data-testid="hidden-steps-summary"
    >
      {stepsHiddenByDisplayOptions > 0 && (
        <DisplayOptionsSummaryItem
          count={stepsHiddenByDisplayOptions}
          onShow={() => setDisplayOptionsOpen(true)}
        />
      )}
      {stepsHiddenByDisplayOptions > 0 && stepsHiddenByFilters > 0 && (
        <span className="text-charcoal-300 mx-2">·</span>
      )}
      {stepsHiddenByFilters > 0 && (
        <FilterSummaryItem
          count={stepsHiddenByFilters}
          isAdditional={stepsHiddenByDisplayOptions > 0}
          onClear={resetFilters}
        />
      )}
    </div>
  );
}

function DisplayOptionsSummaryItem({ count, onShow }: { count: number; onShow?: () => void }) {
  return (
    <span className="inline-flex items-center gap-2" data-testid="display-options-summary">
      <span>
        <span className="text-charcoal-600 font-medium">{count}</span> skipped{" "}
        {count === 1 ? "step" : "steps"} hidden
      </span>
      {onShow && (
        <button
          type="button"
          onClick={onShow}
          className="text-charcoal-600 hover:text-charcoal-800 font-medium"
        >
          Show options
        </button>
      )}
    </span>
  );
}

function FilterSummaryItem({
  count,
  isAdditional,
  onClear,
}: {
  count: number;
  isAdditional: boolean;
  onClear: () => void;
}) {
  return (
    <span className="inline-flex items-center gap-2" data-testid="filter-summary">
      <span>
        <span className="text-charcoal-600 font-medium">{count}</span>{" "}
        {isAdditional && (
          <span className="@xs/outlet:!inline hidden">{count === 1 ? "other " : "others "}</span>
        )}
        hidden by filters
      </span>
      <button
        type="button"
        onClick={onClear}
        className="text-charcoal-600 hover:text-charcoal-800 font-medium"
      >
        Clear filters
      </button>
    </span>
  );
}
